// Utilities for "Pull from Bluebird Writer": read a local Writer folder via
// the File System Access API, work out which Bluebird documents differ from
// their local Markdown counterparts, and apply ONLY individually-approved
// changes through the normal authenticated endpoints.
//
// Design notes:
// - Writer's `.bbw-sync.json` manifest is the map between local files and
//   documents (personal entries are `Type:Name`, workspace entries are
//   prefixed `ws:<id>:` and are out of scope here — Writer pulls those).
// - Comparisons happen in "canonical Markdown": both sides are pushed through
//   the same md→html→md round-trip so formatting quirks of the conversion
//   pipeline don't show up as phantom differences.
// - Writes go through the standard edit endpoint, which snapshots history and
//   enforces optimistic versioning (409 if the document changed meanwhile).

import { Marked } from 'marked';
import TurndownService from 'turndown';
import { gfm } from 'turndown-plugin-gfm';
import { auth } from '../../firebase';

const API_BASE = 'https://bluebirddocumentationadmin.pythonanywhere.com';

// An isolated instance: marked.use() mutates the shared singleton, and BBD's
// document-import path (htmlParsingUtils.js) imports the same module.
//
// breaks: true — a single Enter is a line break, matching Bluebird Writer's
// preview (BBW-3). The two must stay in step or a pushed document would not
// look like what the author saw while writing it.
const marked = new Marked({ gfm: true, breaks: true });

/* ─── Quill HTML → Markdown (same normalization Writer uses) ─────── */

const turndown = new TurndownService({
    headingStyle: 'atx',
    codeBlockStyle: 'fenced',
    bulletListMarker: '-',
    emDelimiter: '*',
});
turndown.use(gfm);

// Keep in sync with bluebird-markdown/src/lib/quillToMarkdown.js — both
// directions of sync must convert identically or diffs report phantoms.
//
// Quill represents a blank line as <p><br></p>; markdown has no equivalent
// (blank lines collapse), so emit a lone <br> paragraph — marked converts
// that straight back to <p><br></p>, making vertical spacing round-trip
// safe instead of silently disappearing (BBW-2).
turndown.addRule('quillBlankLine', {
    filter: (node) =>
        node.nodeName === 'P' &&
        (node.childNodes.length === 0 ||
            (node.childNodes.length === 1 && node.firstChild.nodeName === 'BR')),
    replacement: () => '\n\n<br>\n\n',
});

// Alignment / indent / direction classes live on paragraphs and headings;
// turndown's built-in block rules outrank keep(), so these need a rule of
// their own to survive as literal HTML.
turndown.addRule('quillBlockFormat', {
    filter: (node) =>
        /^(P|H[1-6])$/.test(node.nodeName) &&
        /\bql-(align|indent|direction)-/.test(node.getAttribute('class') || ''),
    replacement: (content, node) => `\n\n${node.outerHTML}\n\n`,
});

// Formats markdown cannot express ride along as literal HTML instead of
// being stripped (BBW-2: highlights vanished on every round trip). Markdown
// permits embedded HTML, marked passes it through, and Quill reads it as its
// own formatting again.
turndown.keep((node) => {
    const name = node.nodeName;
    if (name === 'U' || name === 'SUB' || name === 'SUP') return true;
    // Highlight / text colour, and Quill font & size runs.
    if (name === 'SPAN') {
        return (
            Boolean(node.getAttribute('style')) ||
            /\bql-(font|size)-/.test(node.getAttribute('class') || '')
        );
    }
    return false;
});

function normalizeQuillHtml(html) {
    const doc = new DOMParser().parseFromString(html || '', 'text/html');

    // Per-line Quill code blocks → real <pre><code>
    doc.body.querySelectorAll('.ql-code-block-container').forEach((container) => {
        const lines = [...container.querySelectorAll('.ql-code-block')];
        const lang = lines[0]?.dataset.language;
        const pre = doc.createElement('pre');
        const code = doc.createElement('code');
        if (lang && lang !== 'plain') code.className = `language-${lang}`;
        code.textContent = lines.map((l) => l.textContent).join('\n');
        pre.appendChild(code);
        container.replaceWith(pre);
    });

    // Quill has no concept of "loose" lists, and the gfm task-list rule only
    // recognises a checkbox that sits directly under its <li> — so unwrap the
    // paragraph wrappers markdown round trips introduce (BBW-2 family).
    doc.body.querySelectorAll('li > p').forEach((p) => {
        const li = p.parentNode;
        while (p.firstChild) li.insertBefore(p.firstChild, p);
        p.remove();
    });

    // data-checked lists → GFM task-list form
    doc.body.querySelectorAll('ul[data-checked]').forEach((ul) => {
        const checked = ul.getAttribute('data-checked') === 'true';
        ul.querySelectorAll(':scope > li').forEach((li) => {
            const box = doc.createElement('input');
            box.type = 'checkbox';
            if (checked) box.setAttribute('checked', '');
            li.prepend(box);
        });
    });

    // Flat ql-indent-N lists → nested lists
    doc.body.querySelectorAll('ol, ul').forEach((list) => {
        const items = [...list.children].filter((el) => el.tagName === 'LI');
        if (!items.some((li) => /ql-indent-\d/.test(li.className))) return;
        const root = doc.createElement(list.tagName);
        const stack = [{ level: 0, list: root }];
        let lastLi = null;
        for (const li of items) {
            const match = li.className.match(/ql-indent-(\d+)/);
            const level = match ? Number(match[1]) : 0;
            li.removeAttribute('class');
            while (stack.length > 1 && level < stack[stack.length - 1].level) stack.pop();
            if (level > stack[stack.length - 1].level && lastLi) {
                const sub = doc.createElement(list.tagName);
                lastLi.appendChild(sub);
                stack.push({ level, list: sub });
            }
            stack[stack.length - 1].list.appendChild(li);
            lastLi = li;
        }
        list.replaceWith(root);
    });

    // marked re-parses our lone "<br>" blank-line marker as a top-level HTML
    // block (no <p> wrapper), so canonical round trips see body-level <br>s.
    // Rewrap them so the quillBlankLine rule fires identically every pass.
    [...doc.body.children].forEach((el) => {
        if (el.tagName === 'BR') {
            const p = doc.createElement('p');
            el.replaceWith(p);
            p.appendChild(el);
        }
    });

    doc.body.querySelectorAll('.ql-cursor').forEach((el) => el.remove());
    doc.body.querySelectorAll('iframe').forEach((frame) => {
        const src = frame.getAttribute('src') || '';
        if (src) {
            const link = doc.createElement('a');
            link.href = src;
            link.textContent = src;
            frame.replaceWith(link);
        } else {
            frame.remove();
        }
    });

    return doc.body.innerHTML;
}

export const quillHtmlToMarkdown = (html) => turndown.turndown(normalizeQuillHtml(html));

// marked's GFM task lists use <li><input type=checkbox>…, which Quill does
// not understand (it would silently drop the checkboxes on the next edit).
// Convert them to Quill's own checklist form: one <ul data-checked="…"> per
// run of same-state items, no <input> nodes (BBW-2 family).
export const markdownToHtml = (md) => {
    const doc = new DOMParser().parseFromString(marked.parse(md ?? ''), 'text/html');
    // marked renders ~~strikethrough~~ as <del>, but Quill's Strike blot only
    // recognises <s>/<strike> — a pushed <del> displays fine yet silently
    // vanishes the next time the document is edited in Quill (BBW-2 family).
    doc.body.querySelectorAll('del').forEach((del) => {
        const s = doc.createElement('s');
        while (del.firstChild) s.appendChild(del.firstChild);
        del.replaceWith(s);
    });
    doc.body.querySelectorAll('ul').forEach((ul) => {
        const items = [...ul.children].filter((el) => el.tagName === 'LI');
        const states = items.map((li) => {
            let scope = li.firstElementChild?.tagName === 'P' ? li.firstElementChild : li;
            const input = scope.firstElementChild;
            return input?.tagName === 'INPUT' && input.type === 'checkbox'
                ? input.hasAttribute('checked')
                : null;
        });
        if (!items.length || states.some((s) => s === null)) return; // not a task list
        const groups = [];
        let current = null;
        items.forEach((li, i) => {
            [...li.children].filter((c) => c.tagName === 'P').forEach((p) => {
                while (p.firstChild) li.insertBefore(p.firstChild, p);
                p.remove();
            });
            const input = li.querySelector(':scope > input[type="checkbox"]');
            if (input) {
                if (input.nextSibling?.nodeType === Node.TEXT_NODE) {
                    input.nextSibling.textContent = input.nextSibling.textContent.replace(/^\s+/, '');
                }
                input.remove();
            }
            const state = String(states[i]);
            if (!current || current.getAttribute('data-checked') !== state) {
                current = doc.createElement('ul');
                current.setAttribute('data-checked', state);
                groups.push(current);
            }
            current.appendChild(li);
        });
        ul.replaceWith(...groups);
    });
    return doc.body.innerHTML;
};

// Both sides of a comparison go through the same md→html→md round-trip so
// conversion artifacts cancel out (the round-trip is idempotent after one
// pass — verified). canonical(local) === canonical(remote) means "applying
// local would change nothing". Comparing raw converted remote against a
// canonicalized local would flag phantom diffs on freshly-pulled files.
export const canonicalMarkdown = (md) => quillHtmlToMarkdown(markdownToHtml(md));
export const remoteToMarkdown = (html) => canonicalMarkdown(quillHtmlToMarkdown(html));

/* ─── Misc ───────────────────────────────────────────────────────── */

// FNV-1a — must match Writer's contentHash so the shared manifest agrees.
export function contentHash(text) {
    let hash = 0x811c9dc5;
    for (let i = 0; i < text.length; i++) {
        hash ^= text.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
    }
    return (hash >>> 0).toString(16);
}

export const supportsFolderAccess = () =>
    typeof window !== 'undefined' && 'showDirectoryPicker' in window;

/* ─── Folder scanning ────────────────────────────────────────────── */

const MAX_DEPTH = 6; // mirror Writer's walker

export async function pickWriterFolder() {
    // readwrite so the manifest can be stamped after an apply; the browser
    // shows its own permission prompt for this.
    return window.showDirectoryPicker({ mode: 'readwrite' });
}

export async function scanWriterFolder(dirHandle) {
    const files = []; // { rel, handle }
    let manifest = null;
    let manifestHandle = null;

    async function walk(handle, prefix, depth) {
        if (depth > MAX_DEPTH) return;
        for await (const [name, child] of handle.entries()) {
            if (child.kind === 'directory') {
                if (!name.startsWith('.') && name !== 'node_modules' && name !== 'target') {
                    await walk(child, `${prefix}${name}/`, depth + 1);
                }
            } else if (/\.(md|markdown)$/i.test(name)) {
                files.push({ rel: `${prefix}${name}`, handle: child });
            } else if (!prefix && name === '.bbw-sync.json') {
                manifestHandle = child;
                try {
                    manifest = JSON.parse(await (await child.getFile()).text());
                } catch {
                    manifest = null;
                }
            }
        }
    }

    await walk(dirHandle, '', 0);
    files.sort((a, b) => a.rel.localeCompare(b.rel));
    if (manifest && typeof manifest.docs !== 'object') manifest = null;
    return { files, manifest, manifestHandle };
}

export const readHandleText = async (handle) => (await handle.getFile()).text();

/* ─── Backend calls (Firebase-authenticated, same shapes the app uses) ── */

async function authHeaders() {
    const user = auth.currentUser;
    const token = user ? await user.getIdToken() : null;
    if (!token) throw new Error('You are not signed in.');
    return {
        'requesting-user-uid': user.uid,
        'requesting-user-email': user.email || '',
        'auth-token': `Bearer ${token}`,
        Authorization: `${token}`,
    };
}

export async function fetchDocument(type, name, uid) {
    const headers = await authHeaders();
    const res = await fetch(
        `${API_BASE}/documentation/${type.toLowerCase()}/${encodeURIComponent(name)}/${uid}/`,
        { headers }
    );
    if (res.status === 403 || res.status === 404) return null; // not found / not yours
    if (!res.ok) throw new Error(`Could not load "${name}" (${res.status}).`);
    return res.json();
}

/**
 * Update a document's content, echoing every other field exactly as the
 * standard editor does — the edit endpoint overwrites all fields it reads.
 * `remote` must be the freshly fetched document (its `version` is the
 * optimistic-concurrency token; a 409 means it changed since the check).
 */
export async function updateDocumentContent(type, name, uid, remote, html) {
    const headers = await authHeaders();
    const payload = {
        type,
        newName: name,
        author: uid,
        isPublic: remote.is_public,
        content: html,
        description: remote.description ?? '',
        completed: remote.completed ?? false,
        version: remote.version,
        customLink: remote.custom_domain ?? '',
        customLinkTitle: remote.custom_domain_title ?? '',
        twitterLink: remote.twitter_link ?? '',
        githubLink: remote.github_link ?? '',
        youtubeLink: remote.youtube_channel ?? '',
        linkedInLink: remote.linkedin_link ?? '',
        discordLink: remote.discord_server ?? '',
        hasPreviousAttachment: true, // never touch attachments from a sync
    };
    const res = await fetch(`${API_BASE}/documentation/edit/${type}/${encodeURIComponent(name)}/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify(payload),
    });
    if (res.status === 409) {
        throw new Error('Changed in Bluebird Documentation while you were reviewing — run the check again.');
    }
    if (res.status === 413) throw new Error('Storage limit exceeded.');
    if (!res.ok) throw new Error(`Update failed (${res.status}).`);
    return res;
}

export async function createDocumentFromMarkdown(name, uid, html) {
    const headers = await authHeaders();
    const payload = {
        type: 'Page',
        isPublic: false,
        emails: [],
        content: html,
        name,
        author: uid,
        completed: false,
        description: '',
        space: '',
        parentPage: '',
    };
    const res = await fetch(`${API_BASE}/documentation/create/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify(payload),
    });
    if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Create failed (${res.status}).`);
    }
    return res;
}

/* ─── Workspace documents (role-checked, Firebase-authenticated) ─── */
// Unlike the personal edit endpoint, the workspace update views are PARTIAL
// updates — only fields present in the payload change — so a content sync
// sends just { content, version }. History snapshots and 409 version
// conflicts are handled server-side.

export async function fetchWorkspaceByDomain(customDomain) {
    const headers = await authHeaders();
    const res = await fetch(`${API_BASE}/workspaces/domain/${encodeURIComponent(customDomain)}/`, { headers });
    if (!res.ok) throw new Error(`Could not load this workspace (${res.status}).`);
    return res.json();
}

export async function listWorkspaceDocuments(workspaceId) {
    const headers = await authHeaders();
    const [sRes, pRes] = await Promise.all([
        fetch(`${API_BASE}/workspaces/${workspaceId}/spaces/`, { headers }),
        fetch(`${API_BASE}/workspaces/${workspaceId}/pages/`, { headers }),
    ]);
    if (!sRes.ok || !pRes.ok) throw new Error('Could not list this workspace\'s documents.');
    return { spaces: await sRes.json(), pages: await pRes.json() };
}

export async function fetchWorkspaceDocDetail(workspaceId, type, docId) {
    const headers = await authHeaders();
    const kind = type === 'Space' ? 'spaces' : 'pages';
    const res = await fetch(`${API_BASE}/workspaces/${workspaceId}/${kind}/${docId}/`, { headers });
    if (!res.ok) throw new Error(`Could not load the document (${res.status}).`);
    return res.json();
}

export async function updateWorkspaceDocContent(workspaceId, type, docId, version, html) {
    const headers = await authHeaders();
    const kind = type === 'Space' ? 'spaces' : 'pages';
    const res = await fetch(`${API_BASE}/workspaces/${workspaceId}/${kind}/${docId}/update/`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ content: html, version }),
    });
    if (res.status === 409) {
        throw new Error('Changed in the workspace while you were reviewing — run the check again.');
    }
    if (res.status === 403) throw new Error('You do not have permission to edit this document.');
    if (res.status === 413) throw new Error('Storage limit exceeded.');
    if (!res.ok) throw new Error(`Update failed (${res.status}).`);
    return res;
}

export async function createWorkspacePage(workspaceId, name, html, spaceId) {
    const headers = await authHeaders();
    const payload = {
        page_name: name,
        content: html,
        description: '',
        is_public: false,
        completed: false,
    };
    if (spaceId) payload.space_id = spaceId;
    const res = await fetch(`${API_BASE}/workspaces/${workspaceId}/pages/create/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify(payload),
    });
    if (res.status === 403) throw new Error('You do not have permission to create documents in this workspace.');
    if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Create failed (${res.status}).`);
    }
    return res;
}

/* ─── Manifest write-back ────────────────────────────────────────── */

// Stamp the manifest so Writer's next pull recognises the applied state
// (same rel/hash/updated_at bookkeeping Writer maintains itself).
export async function stampManifest(manifestHandle, manifest, updates) {
    if (!manifestHandle || !manifest) return false;
    for (const u of updates) {
        manifest.docs[u.key] = {
            rel: u.rel,
            hash: u.hash,
            remoteUpdatedAt: u.remoteUpdatedAt,
            pulledAt: new Date().toISOString(),
        };
    }
    try {
        const writable = await manifestHandle.createWritable();
        await writable.write(JSON.stringify(manifest, null, 2));
        await writable.close();
        return true;
    } catch {
        return false; // read-only grant — Writer will just re-verify next pull
    }
}
